import { type FormikProps, useFormik } from 'formik';
import React, { useCallback } from 'react';
import * as yup from 'yup';

const import_translations_validation_schema = yup.object().shape({
   csv_content: yup.string().trim().required('CSV content is required'),
});

export type ImportTranslationsFormValues = {
   csv_content: string;
};

type Props = {
   onClose: () => void;
   onSave: (values: ImportTranslationsFormValues) => Promise<void>;
};

const csv_example = `namespace,name,en
common,welcome,Welcome
common,logout,Log out`;

const ImportTranslationsModal = ({ onClose, onSave }: Props) => {
   const handle_bg_close = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
         if (event.target instanceof HTMLElement && event.target.id === 'wrapper') {
            onClose();
         }
      },
      [onClose],
   );

   const {
      values,
      errors,
      touched,
      isSubmitting,
      handleBlur,
      handleChange,
      handleSubmit,
   } = useFormik<ImportTranslationsFormValues>({
      initialValues: {
         csv_content: '',
      },
      validationSchema: import_translations_validation_schema,
      onSubmit: onSave,
   });

   return (
      <div
         id='wrapper'
         className='fixed inset-0 z-20 flex items-center justify-center bg-black bg-opacity-25 backdrop-blur-sm'
         onClick={handle_bg_close}
      >
         <div className='flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white px-10 py-5'>
            <div className='flex shrink-0 items-center justify-between'>
               <h1 className='text-xl font-medium'>Import Translations</h1>
               <button
                  className='text-boatpark-black underline'
                  onClick={onClose}
                  type='button'
               >
                  Close
               </button>
            </div>

            <div className='mt-6 rounded-md border border-boatpark-blue/20 bg-boatpark-blue/5 px-4 py-3 text-sm text-boatpark-blue'>
               <p>Paste CSV with columns `namespace`, `name`, `en`.</p>
               <p>Both `,` and `;` separators are supported.</p>
            </div>

            <div className='mt-6 flex flex-1 flex-col gap-y-4 overflow-y-auto pr-2'>
               <FormItem
                  error={touched.csv_content ? errors.csv_content : undefined}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  name='csv_content'
                  value={values.csv_content}
               />
               <div className='rounded-md border border-slate-300 bg-slate-100 px-4 py-3 text-sm text-slate-700'>
                  <div className='mb-2 font-medium text-slate-600'>Example</div>
                  <pre className='whitespace-pre-wrap break-words'>{csv_example}</pre>
               </div>
            </div>

            <div className='mt-8 flex shrink-0 flex-row justify-end'>
               <button
                  onClick={(event) => {
                     event.preventDefault();
                     onClose();
                  }}
                  type='button'
               >
                  <span className='text-red-500 underline'>Cancel</span>
               </button>
               <button
                  className='ml-4 rounded-md border bg-boatpark-blue px-4 py-1'
                  disabled={isSubmitting}
                  onClick={() => handleSubmit()}
                  type='submit'
               >
                  <span className='font-medium text-white'>Import</span>
               </button>
            </div>
         </div>
      </div>
   );
};

type FormItemProps = {
   name: keyof ImportTranslationsFormValues;
   value: string;
   error?: string;
   handleBlur: FormikProps<ImportTranslationsFormValues>['handleBlur'];
   handleChange: FormikProps<ImportTranslationsFormValues>['handleChange'];
};

const FormItem = ({
   name,
   value,
   error,
   handleBlur,
   handleChange,
}: FormItemProps) => {
   return (
      <div>
         <label className='mb-2 block font-medium text-gray-500' htmlFor={name}>
            CSV content
         </label>
         <textarea
            className='min-h-[220px] w-full resize-y rounded border border-black px-4 py-3 font-mono text-sm leading-6'
            id={name}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            spellCheck={false}
            value={value}
         />
         {error ? <div className='mt-1 text-sm text-red-500'>{error}</div> : null}
      </div>
   );
};

export default ImportTranslationsModal;
